// src/pages/PrioritiesPage.tsx
import React, { useState, useEffect } from 'react';
import { habitsApi } from '../api';
import type { Habit } from '../types';

function currentYM() {
  const d = new Date(Date.now() + 7*3600000);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
}

export default function PrioritiesPage() {
  const [habits, setHabits]       = useState<Habit[]>([]);
  const [selected, setSelected]   = useState<string[]>([]);
  const [intention, setIntention] = useState<string>('');
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState('');
  const ym = currentYM();

  useEffect(() => {
    const load = async () => {
      const { habits: h } = await habitsApi.list();
      const active = [...h.priority, ...h.active].filter(x => x.type === 'build');
      setHabits(active);
      setSelected(h.priority.map(x => x.habitId));
      const current = h.priority.find(x => x.isIntention);
      if (current) setIntention(current.habitId);
      setLoading(false);
    };
    load();
  }, []);

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      setSelected(selected.filter(x => x !== id));
      if (intention === id) setIntention('');
    } else if (selected.length < 3) {
      setSelected([...selected, id]);
    }
  };

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await habitsApi.setPriorities(selected, ym);
      if (intention) await habitsApi.setIntention(intention, ym);
      setMsg('Priorities saved!');
    } catch (err: any) { setMsg(err.response?.data?.error || 'Failed'); }
    setSaving(false);
  };

  return (
    <div>
      <div style={{ fontSize:26, fontWeight:600, letterSpacing:-0.5, marginBottom:6 }}>Monthly Priorities</div>
      <div style={{ fontSize:12, color:'var(--text3)', fontFamily:'var(--font-mono)', marginBottom:24 }}>
        {ym} · Select up to 3 priority habits and 1 monthly intention
      </div>

      {loading ? (
        <div style={{ color:'var(--text3)', fontSize:12 }}>loading...</div>
      ) : (
        <>
          <div style={{ fontSize:10, color:'var(--text3)', marginBottom:12, fontFamily:'var(--font-mono)' }}>
            {selected.length}/3 selected
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:24 }}>
            {habits.map(h => {
              const isSelected = selected.includes(h.habitId);
              const isIntention = intention === h.habitId;
              return (
                <div key={h.habitId} style={{
                  display:'flex', alignItems:'center', gap:14,
                  padding:'12px 16px', borderRadius:'var(--radius-md)', cursor:'pointer',
                  background: isSelected ? 'var(--green-dim)' : 'var(--bg2)',
                  border: isSelected ? '1px solid rgba(0,229,160,0.25)' : '1px solid var(--border2)',
                  transition:'all 0.15s',
                }} onClick={() => toggle(h.habitId)}>
                  <div style={{
                    width:20, height:20, borderRadius:4, flexShrink:0,
                    background: isSelected ? 'var(--green)' : 'transparent',
                    border: isSelected ? 'none' : '2px solid var(--border2)',
                    display:'flex', alignItems:'center', justifyContent:'center',
                    color:'#0c0e14', fontSize:12, fontWeight:700,
                  }}>
                    {isSelected ? '✓' : ''}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:13, fontWeight:500, color: isSelected ? 'var(--green)' : 'var(--text)' }}>
                      {h.name}
                    </div>
                    <div style={{ fontSize:10, color:'var(--text3)', fontFamily:'var(--font-mono)', marginTop:2 }}>
                      {h.frequency.replace('_',' ')} · {h.consecutiveMonthsAbove80}/3 months ≥80%
                    </div>
                  </div>
                  {isSelected && (
                    <button onClick={e => { e.stopPropagation(); setIntention(isIntention ? '' : h.habitId); }}
                      style={{
                        padding:'4px 10px', borderRadius:'var(--radius-sm)', fontSize:10,
                        background: isIntention ? 'rgba(255,181,71,0.15)' : 'var(--bg3)',
                        border: isIntention ? '1px solid rgba(255,181,71,0.3)' : '1px solid var(--border)',
                        color: isIntention ? 'var(--orange)' : 'var(--text3)', cursor:'pointer',
                      }}>
                      {isIntention ? '⭐ intention' : 'set intention'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {msg && <div style={{ fontSize:11, color: msg.includes('saved') ? 'var(--green)' : 'var(--red)', marginBottom:12 }}>{msg}</div>}

          <button onClick={save} disabled={saving || selected.length === 0} style={{
            padding:'10px 24px', borderRadius:'var(--radius-sm)', fontSize:13,
            background: selected.length > 0 ? 'var(--green)' : 'var(--bg3)',
            border:'none', color: selected.length > 0 ? '#0c0e14' : 'var(--text3)',
            fontWeight:600, cursor: selected.length > 0 ? 'pointer' : 'not-allowed',
          }}>
            {saving ? 'Saving...' : 'Save priorities'}
          </button>
        </>
      )}
    </div>
  );
}
